import {Alert, Attributes, Button, Card, Flags, Label, openDialog} from 'platform/components';
import {HStack, Link, Right, Show, Text, VStack, Icon} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {Trans} from 'react-i18next';

import {indexBy, isNil} from 'ramda';
import {isFalse, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetWhatsAppConfigQuery, useListEmailCredentialsQuery} from '@dms/api/core';
import {useGetAiLeadSettingsQuery, useUpdateAiLeadSettingsMutation} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {AiLeadCard} from './components/AiLeadCard';
import {AiLeadConfigurationForm} from './components/AiLeadConfigurationForm';
import {AutomatedCommunicationForm} from './components/AutomatedCommunicationForm';
import {automatedCommunicationModeOptions} from './constants/automatedCommunicationModeOptions';

// eslint-disable-next-line complexity -- Pre-existing violation
export function AiLeadManagement() {
  const navigate = useNavigate();
  const {data: aiLeadSettings, isLoading, isError} = useGetAiLeadSettingsQuery();
  const {
    data: whatsAppConfig,
    isLoading: isWhatsAppConfigLoading,
    isError: isWhatsAppConfigError,
  } = useGetWhatsAppConfigQuery();
  const [updateAiLeadSettings, {isLoading: isUpdating}] = useUpdateAiLeadSettingsMutation();
  const [updateWhatsAppSettings, {isLoading: isUpdatingWhatsApp}] =
    useUpdateAiLeadSettingsMutation();
  const [updateAutomatedCommunicationSettings, {isLoading: isUpdatingAutomatedCommunication}] =
    useUpdateAiLeadSettingsMutation();
  const {data: branches} = useBranches();
  const {data: emailCredentials} = useListEmailCredentialsQuery({ownerType: 'tenant'});
  const branchesById = indexBy((branch) => branch.id, branches?.branchListItems ?? []);

  const isWhatsAppIntegrationEnabled = whatsAppConfig?.enabled ?? false;
  const isWhatsAppAiLeadEnabled = aiLeadSettings?.whatsAppEnabled ?? false;
  const canToggleWhatsAppAiLead = isWhatsAppIntegrationEnabled || isWhatsAppAiLeadEnabled;
  const isWhatsAppCardExpanded = isWhatsAppAiLeadEnabled || !isWhatsAppIntegrationEnabled;
  const isIntegrationWarningVisible = isWhatsAppAiLeadEnabled && !isWhatsAppIntegrationEnabled;
  const isAnyAiLeadChannelEnabled = (aiLeadSettings?.enabled ?? false) || isWhatsAppAiLeadEnabled;
  const isAutomatedCommunicationEnabled = aiLeadSettings?.automatedCommunication?.enabled ?? false;
  const automatedCommunication = aiLeadSettings?.automatedCommunication;
  const automatedCommunicationMode = automatedCommunicationModeOptions.find(
    (option) => option.value === automatedCommunication?.mode
  );
  const fallbackCredentials = emailCredentials?.credentials?.find(
    (credential) => credential.id === automatedCommunication?.emailCredentialsId
  );
  const automatedCommunicationPrompt = isNotNil(automatedCommunicationMode)
    ? automatedCommunication?.[automatedCommunicationMode.promptName]
    : null;
  const automatedCommunicationRows = [
    {
      label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationAppliesTo'),
      value: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationAppliesToValue'),
    },
    {
      label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationMode'),
      value: automatedCommunicationMode?.label,
    },
    {
      label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationSentFrom'),
      value: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationSentFromValue'),
    },
    {
      label: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationFallbackRow'),
      value: isNotNil(fallbackCredentials)
        ? `${fallbackCredentials.title} (${fallbackCredentials.emailAddress})`
        : i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationFallbackNotSet'),
    },
  ];
  const automatedCommunicationDescription = match([
    isAnyAiLeadChannelEnabled,
    isAutomatedCommunicationEnabled,
  ])
    .with([false, Pattern._], () =>
      i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationDisabledHint')
    )
    .with([true, true], () =>
      i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationActiveDescription')
    )
    .otherwise(() =>
      i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationEnabledDescription')
    );

  const handleNavigateToWhatsAppSettings = () => navigate(settingsRoutes.whatsApp);

  const handleUpdateAiLeadSettings = () => {
    openDialog(
      <AiLeadConfigurationForm
        data-testid={testIds.settings.aiLeadManagement('leadConfigurationForm')}
      />,
      {
        title: i18n.t('page.settings.labels.aiLeadManagement'),
      }
    );
  };

  const handleToggle = (value: boolean) => {
    if (isFalse(value)) {
      return updateAiLeadSettings({
        enabled: false,
        allowedEmailAddresses: [],
        defaultBranchId: null,
      })
        .unwrap()
        .catch(handleApiError);
    }

    handleUpdateAiLeadSettings();
  };

  const handleOpenAutomatedCommunicationForm = () =>
    openDialog(
      <AutomatedCommunicationForm
        data-testid={testIds.settings.aiLeadManagement('automatedCommunicationForm')}
      />,
      {
        title: i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationEnabled'),
      }
    );

  const handleDisableAutomatedCommunication = () => {
    if (isNil(aiLeadSettings)) {
      return;
    }

    updateAutomatedCommunicationSettings({
      enabled: aiLeadSettings.enabled,
      allowedEmailAddresses: aiLeadSettings.allowedEmailAddresses,
      defaultBranchId: aiLeadSettings.defaultBranchId,
      automatedCommunication: {...aiLeadSettings.automatedCommunication, enabled: false},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleAutomatedCommunicationToggle = (value: boolean) => {
    match(value)
      .with(false, handleDisableAutomatedCommunication)
      .otherwise(handleOpenAutomatedCommunicationForm);
  };

  const handleWhatsAppToggle = (value: boolean) =>
    updateWhatsAppSettings({
      enabled: aiLeadSettings?.enabled ?? false,
      allowedEmailAddresses: aiLeadSettings?.allowedEmailAddresses ?? [],
      defaultBranchId: aiLeadSettings?.defaultBranchId ?? null,
      whatsAppEnabled: value,
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <SettingsTemplate
      header={{title: i18n.t('page.settings.labels.aiLeadManagement')}}
      data-testid="ai-lead-management-settings"
      isLoading={isLoading || isWhatsAppConfigLoading}
      isError={isError || isWhatsAppConfigError}
    >
      <SettingsSection>
        <VStack spacing={6}>
          <AiLeadCard data-testid={testIds.settings.aiLeadManagement('leadCard')} />

          <Card
            control={{
              type: 'switch',
              value: aiLeadSettings?.enabled ?? false,
              onChange: handleToggle,
              isLoading: isUpdating,
            }}
            title={i18n.t('page.settings.labels.aiLeadManagementEnabled')}
            isExpanded={aiLeadSettings?.enabled}
            variant="inlineWhite"
            data-testid={testIds.settings.aiLeadManagement('enabledCard')}
          >
            <VStack spacing={4}>
              <Text
                size="small"
                color="secondary"
                data-testid={testIds.settings.aiLeadManagement('enabledDescription')}
              >
                {i18n.t('page.settings.labels.aiLeadManagementEnabledDescription')}
              </Text>

              <Attributes
                data-testid={testIds.settings.aiLeadManagement('settingsAttributes')}
                size="third"
                rows={[
                  {
                    label: i18n.t('entity.branch.labels.defaultBranch'),
                    value: branchesById[aiLeadSettings?.defaultBranchId ?? '']?.marketingName,
                  },
                  {
                    label: i18n.t('page.settings.labels.ProcessingEmailAddress'),
                    value: aiLeadSettings?.processingEmailAddress,
                  },
                ]}
              />

              <Show when={isNotNilOrEmpty(aiLeadSettings?.allowedEmailAddresses)}>
                <VStack>
                  <Label data-testid={testIds.settings.aiLeadManagement('allowedEmailsLabel')}>
                    {i18n.t('page.settings.labels.AllowedEmailAddresses')}
                  </Label>
                  <Flags
                    shouldWrap
                    flags={
                      aiLeadSettings?.allowedEmailAddresses?.map((item) => ({
                        label: item,
                        colorScheme: 'blue',
                        isSubtle: true,
                      })) ?? []
                    }
                    data-testid={testIds.settings.aiLeadManagement('allowedEmailsFlags')}
                  />
                </VStack>
              </Show>

              <Right>
                <Button
                  title={i18n.t('general.actions.edit')}
                  onClick={handleUpdateAiLeadSettings}
                  data-testid={testIds.settings.aiLeadManagement('editButton')}
                />
              </Right>
            </VStack>
          </Card>

          <VStack spacing={2}>
            <Card
              control={{
                type: 'switch',
                value: isWhatsAppAiLeadEnabled,
                onChange: handleWhatsAppToggle,
                isLoading: isUpdatingWhatsApp,
                isDisabled: !canToggleWhatsAppAiLead,
              }}
              title={i18n.t('page.settings.labels.aiLeadManagementWhatsAppEnabled')}
              isExpanded={isWhatsAppCardExpanded}
              variant="inlineWhite"
              data-testid={testIds.settings.aiLeadManagement('whatsAppEnabledCard')}
            >
              <VStack spacing={4}>
                <Show when={isIntegrationWarningVisible}>
                  <Alert
                    variant="warning"
                    title={i18n.t(
                      'page.settings.labels.aiLeadManagementWhatsAppIntegrationDisabledWarning'
                    )}
                    hyperlinks={[
                      {
                        title: i18n.t(
                          'page.settings.labels.aiLeadManagementWhatsAppIntegrationDisabledWarningLink'
                        ),
                        onClick: handleNavigateToWhatsAppSettings,
                      },
                    ]}
                    data-testid={testIds.settings.aiLeadManagement(
                      'whatsAppIntegrationDisabledWarning'
                    )}
                  />
                </Show>
                <Text
                  size="small"
                  color="secondary"
                  data-testid={testIds.settings.aiLeadManagement('whatsAppEnabledDescription')}
                >
                  {i18n.t('page.settings.labels.aiLeadManagementWhatsAppEnabledDescription')}
                </Text>

                <HStack spacing={2}>
                  <Show when={isWhatsAppIntegrationEnabled}>
                    <Icon value="navigation/check" size={4} color="severity.success" />
                  </Show>
                  <Text
                    size="small"
                    color="secondary"
                    data-testid={testIds.settings.aiLeadManagement('whatsAppIntegrationInfo')}
                  >
                    <Trans
                      i18nKey="page.settings.labels.aiLeadManagementWhatsAppIntegrationInfo"
                      components={{
                        whatsAppSettingsLink: (
                          <Link size="small" onClick={handleNavigateToWhatsAppSettings} />
                        ),
                      }}
                    />
                  </Text>
                </HStack>
              </VStack>
            </Card>
          </VStack>

          <Show whenFeatureEnabled={featureFlags.AI_LEAD_AUTOMATED_COMMUNICATION}>
            <Card
              control={{
                type: 'switch',
                value: isAutomatedCommunicationEnabled,
                onChange: handleAutomatedCommunicationToggle,
                isLoading: isUpdatingAutomatedCommunication,
                isDisabled: !isAnyAiLeadChannelEnabled,
              }}
              title={i18n.t('page.settings.labels.aiLeadManagementAutomatedCommunicationEnabled')}
              isExpanded
              variant="inlineWhite"
              data-testid={testIds.settings.aiLeadManagement('automatedCommunicationEnabledCard')}
            >
              <VStack spacing={4}>
                <Text
                  size="small"
                  color="secondary"
                  data-testid={testIds.settings.aiLeadManagement(
                    'automatedCommunicationEnabledDescription'
                  )}
                >
                  {automatedCommunicationDescription}
                </Text>

                <Show when={isAutomatedCommunicationEnabled}>
                  <VStack spacing={4}>
                    <Show when={isNil(automatedCommunication?.emailCredentialsId)}>
                      <Alert
                        variant="warning"
                        title={i18n.t(
                          'page.settings.labels.aiLeadManagementAutomatedCommunicationMissingFallback'
                        )}
                        message={i18n.t(
                          'page.settings.labels.aiLeadManagementAutomatedCommunicationMissingFallbackDescription'
                        )}
                        data-testid={testIds.settings.aiLeadManagement(
                          'automatedCommunicationMissingFallback'
                        )}
                      />
                    </Show>

                    <Attributes
                      size="third"
                      rows={automatedCommunicationRows}
                      data-testid={testIds.settings.aiLeadManagement(
                        'automatedCommunicationAttributes'
                      )}
                    />

                    <Show when={isNotNilOrEmpty(automatedCommunicationPrompt)}>
                      <VStack spacing={1}>
                        <Label>
                          {i18n.t(
                            'page.settings.labels.aiLeadManagementAutomatedCommunicationPrompt'
                          )}
                        </Label>

                        <Text
                          size="small"
                          color="secondary"
                          data-testid={testIds.settings.aiLeadManagement(
                            'automatedCommunicationPrompt'
                          )}
                        >
                          {automatedCommunicationPrompt}
                        </Text>
                      </VStack>
                    </Show>
                  </VStack>

                  <Right>
                    <Button
                      title={i18n.t('general.actions.edit')}
                      onClick={handleOpenAutomatedCommunicationForm}
                      data-testid={testIds.settings.aiLeadManagement('automatedCommunicationEdit')}
                    />
                  </Right>
                </Show>
              </VStack>
            </Card>
          </Show>
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
